import {
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  Divider,
  Grid,
  IconButton,
  Tooltip
} from "@mui/material"
import CheckIcon from "@mui/icons-material/Check"
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useHistory, useLocation, useParams } from "react-router-dom"
import ConfirmDialog from "src/components/ConfirmDialog/ConfirmDialog"
import editIcon from "src/assets/images/Edit.png"
import GlobalTableDragDrop from "src/components/GlobalTable/GlobalTableDragDrop"
import { v4 as uuidv4 } from "uuid"
import { PATH } from "src/constants/paths"
import {
  setSurveyGroups,
  setSurveyQuestions
} from "src/redux/slices/configSlice"
import { RootState, useAppThunkDispatch } from "src/redux/store"
import { Loader } from "src/utils/reusables/reusable"
import Warning from "@mui/icons-material/Warning"
import AddIcon from "@mui/icons-material/Add"
import trashIcon from "src/assets/images/TrashIcon.svg"
import { updateWarnOnTabChangeToStore } from "../helper"
import { useTranslation } from "react-i18next"
import { Translates } from "src/i18n/i18n"
import { getTranslations } from "src/utils/helper"
import React from "react"
import { ConfigDataHook } from "src/hooks/configurationFileData"
import GlobalTable from "src/components/GlobalTable/GlobalTable"

const GroupsAndQuestions = ({ form, isParentEdit, surveyName, setForm }) => {
  const { t } = useTranslation<any>()
  const [preDeleteRow, setPreDeleteRow] = useState<any>()
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const {
    config: { surveyGroups, surveyQuestions }
  } = ConfigDataHook()

  const configTypeFromStore = useSelector(
    (state: RootState) => state.config?.configType
  )

  const [renderData, setRenderData] = useState([] as any)
  const history = useHistory()
  let search = window.location.search || ""
  const location: any = useLocation()
  let locationState: any = { ...location.state }
  let { storeId }: { storeId: string } = useParams()
  const dispatch = useAppThunkDispatch()

  const combineQuestionsByGroup = (surveyGroups, SurveryQuestions) => {
    // Create a map of groupName to questions
    const groupMap = new Map<string, any[]>()

    for (const question of SurveryQuestions) {
      const groupId = question.GroupId || "0"
      if (!groupMap.has(groupId)) {
        groupMap.set(groupId, [])
      }
      groupMap.get(groupId)!.push(question)
    }

    // Flatten everything
    const combineData: any[] = []

    for (const group of surveyGroups) {
      const groupId = group.GroupId
      combineData.push(group)
      const questions = groupMap.get(groupId) || []
      combineData.push(...questions)
      groupMap.delete(groupId)
    }

    // Add remaining questions under "No Name"
    if (groupMap.has("0")) {
      combineData.push({ GroupName: "No Name" })
      combineData.push(...groupMap.get("0")!)
    }

    return combineData
  }

  useEffect(() => {
    setRenderData(combineQuestionsByGroup(surveyGroups, surveyQuestions))
  }, [surveyGroups, surveyQuestions])

  const displaySpan = (props: any) => {
    let valueToDisplay = props.value
    if (!props.row.original?.QuestionText) {
      valueToDisplay = props.value
    } else if (
      props.column.id === "GroupName" &&
      props.row.original?.QuestionText
    ) {
      valueToDisplay = ""
    }
    return (
      <span
        className={`d-block text-left ${
          !props.row.original.QuestionId && !props.row.original.GroupId
            ? "muted-text"
            : ""
        }`}
      >
        {valueToDisplay}
      </span>
    )
  }

  const displayDisableCheckbox = (props: any) => {
    return (
      <>
        {props.value === undefined ? (
          ""
        ) : (
          <Checkbox
            checked={props.value}
            className={`custom-check light`}
            checkedIcon={<CheckIcon />}
            disabled={true}
          />
        )}
      </>
    )
  }

  const confirmDelete = row => {
    setIsConfirmOpen(true)
    setPreDeleteRow(row)
  }

  const callbackConfirmation = (res: boolean) => {
    if (res) {
      DeleteGroupOrQuestion(preDeleteRow)
    }
    setIsConfirmOpen(false)
  }

  const DeleteGroupOrQuestion = data => {
    if (data.QuestionId) {
      const remainingQuestions = surveyQuestions.filter(
        que => que.QuestionId !== data.QuestionId
      )
      dispatch(setSurveyQuestions(remainingQuestions))
    } else {
      const remainingGroups = surveyGroups.filter(
        gp => gp.GroupId !== data.GroupId
      )
      const questions = surveyQuestions.map(que =>
        que.GroupId === data.GroupId ? { ...que, GroupId: 0 } : que
      )
      dispatch(setSurveyQuestions(questions))
      dispatch(setSurveyGroups(remainingGroups))
    }
  }

  const setContent = () => {
    return (
      <Alert severity="error" icon={<Warning />} className="custom-alert">
        <AlertTitle className="alert__title">
          <h2>{getTranslations(t, Translates.Warning)}</h2>
        </AlertTitle>
        <p>
          {" "}
          <strong className="d-block">{`${getTranslations(
            t,
            "Are_you_sure"
          )}`}</strong>
        </p>
      </Alert>
    )
  }

  const CopyGroupOrQuestion = (row: any) => {
    if (row.QuestionId) {
      const _item = {
        ...row,
        ...{
          QuestionText: `Copy of ${row.QuestionText}`,
          // QuestionId: (surveyQuestions?.length ?? 0) + 1
          QuestionId: uuidv4()
        }
      }
      dispatch(setSurveyQuestions([...surveyQuestions, _item]))
    } else if (row.GroupId) {
      // const newGroupId = (surveyGroups?.length ?? 0) + 1
      const newGroupId = uuidv4()
      const _item = {
        ...row,
        ...{
          GroupName: `Copy of ${row.GroupName}`,
          GroupId: newGroupId
        }
      }
      const copiedSurveyQuestions = surveyQuestions
        .filter(que => row.GroupId === que.GroupId)
        .map(que => ({
          ...que,
          GroupId: newGroupId
        }))
      dispatch(
        setSurveyQuestions([...surveyQuestions, ...copiedSurveyQuestions])
      )
      dispatch(setSurveyGroups([...surveyGroups, _item]))
    }
  }

  const EditGroupOrQuestion = (data: any) => {
    updateWarnOnTabChangeToStore(false)
    setTimeout(() => {
      history.push(
        (data.QuestionText
          ? PATH.CREATE_EDIT_SURVERYS_QUESTION
          : PATH.CREATE_EDIT_SURVERYS_GROUP) + search,
        {
          isEdit: true,
          data: { ...data },
          storeId: storeId,
          surveyName: surveyName,
          isParentEdit: isParentEdit,
          form: form
        }
      )
    })
  }

  const columnsData = React.useMemo(
    () => [
      // {
      //   Header: getTranslations(t, Translates.Sort),
      //   accessor: "SortOrder",
      //   headerClassName: "temp_class",
      //   disableFilters: true,
      //   disableSortBy: true,
      //   setWidth: 50,
      //   Cell: props => {
      //     return ''
      //   }
      // },
      {
        Header: getTranslations(t, Translates.Group_Name),
        accessor: "GroupName",
        headerClassName: "temp_class",
        disableFilters: true,
        disableSortBy: true,
        setWidth: 150,
        Cell: props => {
          return displaySpan(props)
        }
      },
      {
        Header: getTranslations(t, Translates.Group_Label),
        accessor: "GroupLabel",
        headerClassName: "temp_class",
        disableFilters: true,
        disableSortBy: true,
        setWidth: 150,
        Cell: props => {
          return displaySpan(props)
        }
      },
      {
        Header: getTranslations(t, Translates.Question_Text),
        accessor: "QuestionText",
        headerClassName: "temp_class",
        disableFilters: true,
        disableSortBy: true,
        setWidth: 750,
        Cell: props => {
          return displaySpan(props)
        }
      },
      {
        Header: getTranslations(t, Translates.Response_Type),
        accessor: "ResponseType",
        headerClassName: "temp_class",
        disableFilters: true,
        disableSortBy: true,
        setWidth: 200,
        Cell: props => {
          return displaySpan(props)
        }
      },

      {
        Header: getTranslations(t, Translates.Required),
        accessor: "ResponseRequired",
        headerClassName: "temp_class",
        disableFilters: true,
        disableSortBy: true,
        setWidth: 50,
        Cell: props => {
          return displayDisableCheckbox(props)
        }
      },

      {
        Header: getTranslations(t, Translates.ACTIONS),
        accessor: "action",
        headerClassName: "temp_class",
        disableFilters: true,
        disableSortBy: true,
        setWidth: 180,
        Cell: props => {
          return (
            <>
              {(props.row.original.QuestionId ||
                props.row.original.GroupId) && (
                <Tooltip arrow title={getTranslations(t, Translates.Copy)}>
                  <IconButton
                    onClick={() => {
                      CopyGroupOrQuestion(props.row.original)
                    }}
                    data-testid="copy-btn"
                    disabled={configTypeFromStore === "view" ? true : false}
                  >
                    <span className="action-btns text-center d-block ">
                      <ContentCopyOutlinedIcon
                        className={`copy-icon ${
                          configTypeFromStore === "view"
                            ? "disable-img"
                            : "pointer"
                        }`}
                      />
                    </span>
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip arrow title={getTranslations(t, Translates.Edit)}>
                <IconButton
                  onClick={() => {
                    EditGroupOrQuestion(props.row.original)
                  }}
                  data-testid="edit-btn"
                >
                  <span className="action-btns text-center d-block ">
                    <img src={editIcon} alt={"edit icon"} className="pointer" />
                  </span>
                </IconButton>
              </Tooltip>
              <Tooltip arrow title={getTranslations(t, Translates.Delete)}>
                <IconButton
                  onClick={() => confirmDelete(props.row.original)}
                  data-testid="delete-btn"
                  disabled={configTypeFromStore === "view" ? true : false}
                >
                  <span className="action-btns text-center d-block ">
                    <img
                      src={trashIcon}
                      alt="delete"
                      className={`${
                        configTypeFromStore === "view"
                          ? "disable-img"
                          : "pointer"
                      }`}
                    />
                  </span>
                </IconButton>
              </Tooltip>
            </>
          )
        }
      }
    ],
    [renderData, t]
  )

  const displayGrid = () => {
    return (
      <Grid xs={12} item data-testid="surveysGlobTable">
        <GlobalTable
          component="surveys"
          columns={columnsData}
          data={renderData || []}
          noDataText={
            renderData?.length && renderData?.length !== 0
              ? Loader()
              : `${getTranslations(t, Translates.No_Questions_Available)}.`
          }
        ></GlobalTable>
      </Grid>
    )
  }

  const handleNewGroupForSurvery = () => {
    updateWarnOnTabChangeToStore(false)
    form = {
      ...form
    }
    history.push(PATH.CREATE_EDIT_SURVERYS_GROUP + search, {
      isParentEdit: isParentEdit,
      isGrandTotalEdit: false,
      surveyName: surveyName,
      form: form,
      data: locationState.data
    })
  }

  const handleNewQuestionForSurvery = () => {
    updateWarnOnTabChangeToStore(false)
    form = {
      ...form
    }
    history.push(PATH.CREATE_EDIT_SURVERYS_QUESTION + search, {
      isParentEdit: isParentEdit,
      isGrandTotalEdit: false,
      form: form,
      surveyName: surveyName,
      data: locationState.data
    })
  }

  return (
    <>
      <Grid
        container
        className="custom-table-responsive categories-table"
        data-testid="reportings-prompt-display-grid"
      >
        <Grid item xs={12} sm={12}>
          <Divider className="dashed my-4" />
          <Grid container>
            <Grid item xs={6}>
              <h4>{getTranslations(t, Translates.Questions)}</h4>
            </Grid>
            <Grid item xs={6} className="text-right">
              <Button
                variant="contained"
                data-testid="createGroup"
                className="primary-btn pa-2"
                onClick={handleNewGroupForSurvery}
                disabled={!surveyName}
              >
                <AddIcon className="mr-1" />{" "}
                {getTranslations(t, Translates.New_Group)}
              </Button>
              <Button
                variant="contained"
                data-testid="createQuestion"
                className="primary-btn pa-2 ml-1"
                onClick={handleNewQuestionForSurvery}
                disabled={!surveyName}
              >
                <AddIcon className="mr-1" />{" "}
                {getTranslations(t, Translates.New_Question)}
              </Button>
            </Grid>
            <Grid item xs={12} sm={12} className="mt-2">
              {displayGrid()}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {isConfirmOpen && (
        <ConfirmDialog
          open={isConfirmOpen}
          callBack={callbackConfirmation}
          title={getTranslations(t, Translates.DELETE)}
          content={setContent}
          custref={{}}
          confirmButtonText={getTranslations(t, Translates.CONFIRM)}
          cancelButtonText={getTranslations(t, Translates.Cancel)}
        ></ConfirmDialog>
      )}
    </>
  )
}

export default GroupsAndQuestions
